<template>
  <MarketingHeader>
    <div class="bento-item" role="region" aria-label="Résultats moyens">
      <div class="head">
        <h3>Performance</h3>
        <span v-if="report" class="sub">{{ report.count }} publication{{ report.count > 1 ? 's' : '' }} avec résultats</span>
      </div>

      <p v-if="loadError" class="state">Les résultats n'ont pas pu être chargés.</p>
      <p v-else-if="!report" class="state">Chargement…</p>
      <template v-else-if="report.count === 0">
        <p class="state">Aucun résultat saisi pour l'instant. Ouvrez une publication publiée, onglet « Résultats », et recopiez les chiffres de LinkedIn : les moyennes et le classement apparaissent ici.</p>
        <button type="button" class="panel-button" @click="$router.push('/marketing-publications')">Aller aux publications</button>
      </template>
      <template v-else>
        <div class="kpis">
          <div v-for="metric in metrics" :key="metric.key" class="kpi" :title="metric.help || metric.label">
            <span class="kpi-count">{{ formatAverage(report.averages.all[metric.key]) }}</span>
            <span class="kpi-label">{{ metric.label }} <small>par publication</small></span>
          </div>
        </div>
        <p class="note">Moyennes sur les seules publications où le chiffre est connu : un chiffre non saisi n'est pas compté comme zéro.</p>
      </template>
    </div>

    <template v-if="report && report.count > 0">
      <div class="bento-item" role="region" aria-label="Les publications les plus vues">
        <div class="head"><h3>Les plus vues</h3><span class="sub">classées par impressions</span></div>
        <table v-if="report.top.length > 0" class="top">
          <thead>
            <tr>
              <th class="name">Sujet</th>
              <th class="hide-for-mobile">Intention</th>
              <th class="hide-for-mobile">Publié le</th>
              <th v-for="metric in metrics" :key="metric.key" class="number" :class="{'hide-for-mobile': metric.key !== 'impressions' && metric.key !== 'reactions'}">{{ metric.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in report.top" :key="row.publicationId" class="clickable" @click="$refs.publicationModal.openModal(row.publicationId, 'results')">
              <td class="name" :title="row.title">{{ row.title }}<small>{{ channelLabel(row.channel) }}</small></td>
              <td class="hide-for-mobile"><FunnelStageBadge :stage="row.funnelStage" /></td>
              <td class="hide-for-mobile">{{ row.publishedAt ? formatDate(row.publishedAt) : 'Date non définie' }}</td>
              <td v-for="metric in metrics" :key="metric.key" class="number" :class="{'hide-for-mobile': metric.key !== 'impressions' && metric.key !== 'reactions'}">{{ formatNumber(row.metrics[metric.key]) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="state">Aucune publication n'a d'impressions saisies.</p>
      </div>

      <div class="bento-item" role="region" aria-label="Comparaisons par catégorie">
        <div class="head"><h3>Ce qui marche le mieux</h3></div>
        <p class="note first">À quelques publications par mois, ces moyennes sont des tendances, pas des vérités : une ligne estompée repose sur moins de trois publications.</p>
        <div class="breakdowns">
          <PerformanceBreakdown title="Selon l'intention" first-column="Intention" :rows="stageRows" />
          <PerformanceBreakdown title="Selon le rythme" first-column="Rythme" :rows="cadenceRows" />
          <PerformanceBreakdown title="Selon la structure du texte" first-column="Structure" :rows="templateRows" />
          <PerformanceBreakdown title="Avec ou sans image" first-column="Image" :rows="imageRows" />
        </div>
      </div>
    </template>

    <PublicationModal ref="publicationModal" :callback="load" />
  </MarketingHeader>
</template>

<script>
import MarketingHeader from "@/marketing/components/MarketingHeader.vue";
import FunnelStageBadge from "@/marketing/components/FunnelStageBadge.vue";
import PerformanceBreakdown from "@/marketing/components/PerformanceBreakdown.vue";
import PublicationModal from "@/marketing/components/modals/PublicationModal.vue";
import {StatsController} from "@/marketing/controllers/StatsController";
import {PromptController} from "@/marketing/controllers/PromptController";
import {CADENCE_ENUM, CHANNEL_ENUM, FUNNEL_STAGE_FILTER_ENUM, METRICS, formatAverage, formatDate, formatNumber, labelOf} from "@/marketing/utils";

/**
 * Écran « Performance » : moyennes des résultats saisis, classement des publications les plus vues et comparaisons par catégorie.
 * Ces chiffres informent : l'intention d'un contenu reste celle que vous avez choisie, jamais déduite d'eux.
 */
export default {
  name: 'PerformanceView',
  components: {MarketingHeader, FunnelStageBadge, PerformanceBreakdown, PublicationModal},
  data() {
    return {
      metrics: METRICS,
      report: undefined,
      loadError: false,
      templates: [],
    };
  },
  computed: {
    stageRows() {
      return this.rowsOf(this.report?.averages.byFunnelStage, Object.values(FUNNEL_STAGE_FILTER_ENUM), 'À classer');
    },
    cadenceRows() {
      return this.rowsOf(this.report?.averages.byCadence, Object.values(CADENCE_ENUM), 'Sans rythme');
    },
    templateRows() {
      return this.rowsOf(this.report?.averages.byTemplate, this.templates.map(template => ({value: template.id, label: template.label})), 'Sans structure choisie');
    },
    imageRows() {
      const image = this.report?.averages.byImage;
      if (!image) return [];
      return [
        {key: 'with', label: 'Avec une image', averages: image.withImage},
        {key: 'without', label: 'Sans image', averages: image.withoutImage},
      ].filter(row => row.averages.count > 0);
    },
  },
  async mounted() {
    const templates = await PromptController.getTemplates();
    if (Array.isArray(templates)) this.templates = templates;
    await this.load();
  },
  methods: {
    formatAverage,
    formatDate,
    formatNumber,
    channelLabel(channel) {
      return labelOf(CHANNEL_ENUM, channel, channel);
    },
    // Une ligne par catégorie connue (dans l'ordre du catalogue), puis les catégories inattendues telles quelles
    rowsOf(groups, known, fallbackLabel) {
      if (!groups) return [];
      const rows = known.filter(item => groups[item.value]).map(item => ({key: item.value, label: item.label, averages: groups[item.value]}));
      Object.keys(groups).filter(key => !known.some(item => item.value === key)).forEach(key => rows.push({key, label: key || fallbackLabel, averages: groups[key]}));
      return rows;
    },
    async load() {
      const report = await StatsController.performance();
      this.loadError = !report || !!report.errors;
      this.report = this.loadError ? undefined : report;
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/marketing/style/modal.styl';

.head
  display flex
  align-items center
  flex-wrap wrap
  gap 10px 12px
  margin-bottom 14px

  h3
    margin 0
    font-size 1.05em
    text-transform uppercase
    color THEME_COLOR_PRIMARY

  .sub
    padding 3px 10px
    border-radius 999px
    background #E4EEFC
    color #2F5FA8
    font-size 0.78em
    font-weight 700

.state
  margin 0 0 12px
  color CTRL_TEXT_MUTED

.note
  margin 12px 2px 0
  color CTRL_TEXT_MUTED
  font-size 0.85em

  &.first
    margin-top 0
    margin-bottom 14px

.kpis
  display grid
  grid-template-columns repeat(auto-fit, minmax(150px, 1fr))
  gap 10px

.kpi
  display flex
  flex-direction column
  gap 2px
  padding 12px 16px
  border 1px solid #D6E3F5
  border-radius 14px
  background-image linear-gradient(160deg, #FAFCFF, #EDF3FD)

.kpi-count
  font-size 1.8em
  font-weight 800
  line-height 1.1
  color #1F3A63
  font-variant-numeric tabular-nums

.kpi-label
  font-size 0.8em
  font-weight 600
  color #5A6F8F

  small
    font-weight 400

.top
  width 100%
  border-collapse collapse
  font-size 14px

  th
    padding 8px 10px
    border-bottom 2px solid THEME_COLOR_PRIMARY
    color THEME_COLOR_PRIMARY
    font-size 0.9em
    text-align left

  td
    padding 6px 10px
    border-bottom 1px solid PAGE_RULE_SOFT
    color #3f3931

  .number
    text-align right
    font-variant-numeric tabular-nums

  th.number
    text-align right

  .name
    max-width 320px
    overflow hidden
    text-overflow ellipsis
    white-space nowrap
    font-weight 600

    small
      display block
      color CTRL_TEXT_MUTED
      font-weight 400

  .clickable
    cursor pointer

    &:hover
      background PAGE_LEMON_HOVER

.breakdowns
  display grid
  grid-template-columns repeat(auto-fit, minmax(420px, 1fr))
  gap 22px 28px

@media (max-width: 760px)
  .hide-for-mobile
    display none
  .breakdowns
    grid-template-columns 1fr
</style>
